/**
 * Phase 9 Session A (gaps XC-1, XC-4) — shared stale-while-revalidate hook
 * that polls the pending-count endpoints for:
 *   - GET /api/v1/admin/rules/candidates?status=pending&limit=1
 *   - GET /api/v1/admin/rules/suggestions?status=pending&limit=1
 *
 * Both endpoints return the `Paginated` envelope, so we only care about
 * `total`. Polling runs every 30 seconds, which matches the Langfuse
 * trace-surfacing cadence on the dashboard cards. Cleanup cancels the
 * interval + marks the hook as unmounted so no late `setState` fires on a
 * dead component.
 *
 * Two consumers:
 *   1. `AdminHomePage` tiles (badges for Candidates / Suggestions).
 *   2. `Sidebar` nav entries (inline badges in the Admin group).
 *
 * Endpoint response envelope matches `Paginated<T>` from `ruleIngestion.ts`:
 *   { data: T[], total: number, limit: number, offset: number }
 */
import { useEffect, useState } from 'react';
import { apiGet } from '../lib/api.js';

const POLL_INTERVAL_MS = 30_000;

interface CountEnvelope {
  total: number;
}

export interface AdminCounts {
  candidatesPending: number | null;
  suggestionsPending: number | null;
  // Spec 103 — pending rule-proposals badge (Observatory sidebar + admin home).
  proposalsPending: number | null;
  error: string | null;
}

export function useAdminCounts(): AdminCounts {
  const [counts, setCounts] = useState<AdminCounts>({
    candidatesPending: null,
    suggestionsPending: null,
    proposalsPending: null,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const [candidates, suggestions, proposals] = await Promise.all([
          apiGet<CountEnvelope>(
            '/api/v1/admin/rules/candidates?status=pending&limit=1',
          ),
          apiGet<CountEnvelope>(
            '/api/v1/admin/rules/suggestions?status=pending&limit=1',
          ),
          apiGet<CountEnvelope>(
            '/api/v1/admin/rules/proposals?status=pending&limit=1',
          ),
        ]);
        if (cancelled) return;
        setCounts({
          candidatesPending: candidates.total ?? 0,
          suggestionsPending: suggestions.total ?? 0,
          proposalsPending: proposals.total ?? 0,
          error: null,
        });
      } catch (e) {
        if (cancelled) return;
        setCounts((prev) => ({
          ...prev,
          error: e instanceof Error ? e.message : String(e),
        }));
      }
    };

    void load();
    const id = window.setInterval(() => {
      void load();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  return counts;
}
